import {
  geocodeByAddress,
  geocodeByPlaceId,
  getLatLng,
} from "react-places-autocomplete";

/**
 * Turn a place the user picked into the values `city`, `state` and `country`
 * actually mean.
 *
 * Every location field in this app used to do the same thing by hand:
 *
 *     const city    = address.split(",")[0].trim();
 *     const state   = address.split(",")[1].trim();
 *     const country = address.split(",")[2].trim();
 *
 * That reads Google's display string as if it always had exactly three parts.
 * It often doesn't. "Chandigarh, India" has two, so `state` came out as
 * "India" and `country` as undefined — and a two-part description throws
 * outright on the `.trim()` of a missing segment. Districts and union
 * territories produce four.
 *
 * The address components carry the same information already labelled, so
 * nothing has to be inferred from position.
 *
 * The `placeId` matters as much as the names. The backend matches on it before
 * anything else, which is what connects a new entry to the row we already hold
 * rather than creating a second one beside it — "Bengaluru" and "Bangalore"
 * were one city long before the database agreed. It is also the backend's
 * signal that `state` and `country` came from address components and can be
 * trusted to create rows; without it they are matched against existing rows
 * but never used to make new ones.
 */

function component(result, type) {
  return result?.address_components?.find((c) => c.types?.includes(type))
    ?.long_name;
}

/**
 * `placeId` is preferred over `address` because it identifies one place, where
 * a display string is a search that can land somewhere else. Falls back to the
 * address for callers that don't have it yet.
 *
 * Returns null when the lookup finds nothing, so callers can leave their state
 * untouched rather than storing a half-filled place.
 */
export async function resolvePlace(address, placeId) {
  const results = placeId
    ? await geocodeByPlaceId(placeId)
    : await geocodeByAddress(address);

  const result = results?.[0];
  if (!result) return null;

  const { lat, lng } = await getLatLng(result);

  return {
    /**
     * `locality` is the city. Not every place has one — pick a district and
     * there is no locality to read — so the first segment of the display
     * string stays as the fallback, which is what this always used to use.
     */
    city: component(result, "locality") || address?.split(",")[0]?.trim() || "",
    state: component(result, "administrative_area_level_1"),
    country: component(result, "country"),
    lat,
    lng,
    placeId: placeId || result.place_id,
  };
}
